<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import {
  buildArrivalWindow,
  groupPurchaseOrdersByDistributionCenter,
  IN_PROCESS_WINDOWS,
  totalPurchaseOrderCount,
  type DistributionCenterActivityRow,
  type InProcessWindow,
} from "../../features/dashboard/distributionCenterActivity.ts";
import { searchPurchaseOrders } from "../../services/purchaseOrderService.ts";

// Live open-PO counts per distribution center, so the landing page says
// something true on arrival instead of only after a run. This is the one card
// here that does not read from the mock scenarios.
const selectedWindow = ref<InProcessWindow>(
  IN_PROCESS_WINDOWS[0] ?? { id: "six-weeks", label: "6 weeks", days: 42 }
);
const rows = ref<DistributionCenterActivityRow[]>([]);
const loading = ref(false);
const errorMessage = ref<string | undefined>(undefined);
const lastUpdatedAt = ref<Date | undefined>(undefined);

const totalCount = computed(() => totalPurchaseOrderCount(rows.value));

const asOfLabel = computed(() => {
  const updatedAt = lastUpdatedAt.value;
  if (!updatedAt) {
    return "";
  }
  return `As of ${updatedAt.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  })}`;
});

// The counts are only meaningful next to the window they were taken over, so
// the window is stated in the card rather than left to the toggle alone.
const windowLabel = computed(
  () => `Open POs arriving in the next ${selectedWindow.value.label}`
);

async function loadCounts(): Promise<void> {
  loading.value = true;
  errorMessage.value = undefined;

  try {
    const window = buildArrivalWindow(selectedWindow.value.days, new Date());
    const purchaseOrders = await searchPurchaseOrders(window);
    rows.value = groupPurchaseOrdersByDistributionCenter(purchaseOrders);
    lastUpdatedAt.value = new Date();
  } catch {
    // The exact API error is not actionable for a buyer; the retry is.
    rows.value = [];
    lastUpdatedAt.value = undefined;
    errorMessage.value = "Could not load purchase order counts.";
  } finally {
    loading.value = false;
  }
}

function selectWindow(window: InProcessWindow): void {
  if (window.id === selectedWindow.value.id) {
    return;
  }
  selectedWindow.value = window;
  void loadCounts();
}

onMounted(() => {
  void loadCounts();
});
</script>

<template>
  <section class="dashboard-card dc-activity-card rounded-[1.5rem] px-5 py-4">
    <header class="dc-activity-header">
      <div class="min-w-0">
        <h2 class="dc-activity-title">In Process by Distribution Center</h2>
        <p class="dc-activity-subtitle">{{ windowLabel }}</p>
      </div>

      <div class="dc-activity-controls">
        <div
          class="dc-activity-toggle"
          role="group"
          aria-label="Arrival window"
        >
          <button
            v-for="window in IN_PROCESS_WINDOWS"
            :key="window.id"
            type="button"
            class="dc-activity-toggle-option"
            :class="{
              'dc-activity-toggle-option--selected':
                window.id === selectedWindow.id,
            }"
            :aria-pressed="window.id === selectedWindow.id"
            @click="selectWindow(window)"
          >
            {{ window.label }}
          </button>
        </div>
        <p v-if="asOfLabel" class="dc-activity-as-of">{{ asOfLabel }}</p>
      </div>
    </header>

    <p
      v-if="loading"
      class="dc-activity-status"
      role="status"
      aria-live="polite"
    >
      Loading purchase order counts…
    </p>

    <div v-else-if="errorMessage" class="dc-activity-status" role="alert">
      {{ errorMessage }}
      <button type="button" class="dc-activity-retry" @click="loadCounts">
        Try again
      </button>
    </div>

    <p v-else-if="totalCount === 0" class="dc-activity-status">
      No open purchase orders arrive in this window.
    </p>

    <ul v-else class="dc-activity-list">
      <li
        v-for="row in rows"
        :key="row.division"
        class="dashboard-card--inner dc-activity-tile"
      >
        <p class="dc-activity-tile-label">{{ row.label }}</p>
        <p class="dc-activity-tile-value">{{ row.purchaseOrderCount }}</p>
        <p class="dc-activity-tile-detail">
          {{
            row.purchaseOrderCount === 1 ? "purchase order" : "purchase orders"
          }}
        </p>
      </li>
    </ul>
  </section>
</template>
